import { c as _c } from "react/compiler-runtime";
import { getWaveReadProxyRoleRequestKey, getWaveReadRequestKey, isWaveReadJwtExpired } from "@/hooks/useMarkWaveNotificationsRead.identity";
import type { WaveReadTemporaryProxyRoleIdentity, WaveReadVerifiedIdentity } from "@/hooks/useMarkWaveNotificationsRead.identity";
import { clearAllWaveReadState, clearPendingWaveReadsForAddress, deleteLatestVerifiedWaveReadIdentityByAddress, deleteLatestVerifiedWaveReadIdentityIfCurrent, enqueuePendingWaveReadRequest, flushPendingClearedWaveReadRequests, flushPendingWaveReadRequests, getLatestVerifiedWaveReadIdentityByAddress, getStaleAddressEpochWaveReadResult, getVerifiedProxyRoleIdentityKey, markWaveReadIdentityCleared, markWaveReadWithAuthHeaders, setLatestVerifiedWaveReadIdentityByAddress } from "@/hooks/useMarkWaveNotificationsRead.requests";
import type { MarkWaveNotificationsReadOptions, MarkWaveNotificationsReadResult, WaveReadAddressEpoch, WaveReadCacheRefs, WaveReadSendIntent, WaveReadSendRetryContext } from "@/hooks/useMarkWaveNotificationsRead.types";
import { useLayoutEffect, useMemo, useRef } from "react";
let mountedWaveNotificationsReadMarkerHookCount = 0;
let clearWaveNotificationsReadStateTimeout: {
  readonly timeout: ReturnType<typeof globalThis.setTimeout>;
  readonly addressKey: string | null;
} | null = null;
export const useWaveReadCacheRefs = t0 => {
  const $ = _c(21);
  const {
    addressEpoch,
    identityKey,
    temporaryProxyRoleIdentity,
    verifiedIdentity,
    invalidateNotifications
  } = t0;
  const invalidateNotificationsRef = useRef(invalidateNotifications);
  const latestAddressEpochRef = useRef(addressEpoch);
  let t1;
  if ($[0] !== identityKey || $[1] !== verifiedIdentity) {
    t1 = new Map(verifiedIdentity ? [[identityKey, verifiedIdentity]] : []);
    $[0] = identityKey;
    $[1] = verifiedIdentity;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const authByIdentityRef = useRef(t1);
  let t2;
  if ($[3] !== identityKey || $[4] !== temporaryProxyRoleIdentity) {
    t2 = new Map(temporaryProxyRoleIdentity ? [[identityKey, temporaryProxyRoleIdentity]] : []);
    $[3] = identityKey;
    $[4] = temporaryProxyRoleIdentity;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const temporaryProxyRoleIdentityByIdentityRef = useRef(t2);
  let t3;
  if ($[6] !== verifiedIdentity) {
    t3 = new Map(verifiedIdentity ? [[verifiedIdentity.addressKey, verifiedIdentity]] : []);
    $[6] = verifiedIdentity;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const latestVerifiedIdentityByAddressRef = useRef(t3);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Map();
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const latestVerifiedIdentityByProxyRoleRef = useRef(t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Set();
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const clearedIdentityKeysRef = useRef(t5);
  let t6;
  let t7;
  if ($[10] !== invalidateNotifications) {
    t6 = () => {
      invalidateNotificationsRef.current = invalidateNotifications;
    };
    t7 = [invalidateNotifications];
    $[10] = invalidateNotifications;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useLayoutEffect(t6, t7);
  let t8;
  let t9;
  if ($[13] !== addressEpoch) {
    t8 = () => {
      latestAddressEpochRef.current = addressEpoch;
    };
    t9 = [addressEpoch];
    $[13] = addressEpoch;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useLayoutEffect(t8, t9);
  let t10;
  let t11;
  if ($[16] !== identityKey || $[17] !== temporaryProxyRoleIdentity) {
    t10 = () => {
      if (temporaryProxyRoleIdentity) {
        temporaryProxyRoleIdentityByIdentityRef.current.set(identityKey, temporaryProxyRoleIdentity);
        return;
      }
      temporaryProxyRoleIdentityByIdentityRef.current.delete(identityKey);
    };
    t11 = [identityKey, temporaryProxyRoleIdentity];
    $[16] = identityKey;
    $[17] = temporaryProxyRoleIdentity;
    $[18] = t10;
    $[19] = t11;
  } else {
    t10 = $[18];
    t11 = $[19];
  }
  useLayoutEffect(t10, t11);
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      invalidateNotificationsRef,
      latestAddressEpochRef,
      authByIdentityRef,
      temporaryProxyRoleIdentityByIdentityRef,
      latestVerifiedIdentityByAddressRef,
      latestVerifiedIdentityByProxyRoleRef,
      clearedIdentityKeysRef
    };
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
};
export const useSyncWaveReadVerifiedIdentityCaches = t0 => {
  const $ = _c(9);
  const {
    walletAuth,
    verifiedIdentity,
    cacheRefs
  } = t0;
  const {
    authByIdentityRef,
    clearedIdentityKeysRef,
    invalidateNotificationsRef,
    latestVerifiedIdentityByAddressRef,
    latestVerifiedIdentityByProxyRoleRef
  } = cacheRefs;
  let t1;
  let t2;
  if ($[0] !== authByIdentityRef || $[1] !== clearedIdentityKeysRef || $[2] !== invalidateNotificationsRef || $[3] !== latestVerifiedIdentityByAddressRef || $[4] !== latestVerifiedIdentityByProxyRoleRef || $[5] !== verifiedIdentity || $[6] !== walletAuth) {
    t1 = () => {
      if (walletAuth === null) {
        for (const identity of authByIdentityRef.current.values()) {
          const proxyRoleIdentityKey = getVerifiedProxyRoleIdentityKey(identity);
          markWaveReadIdentityCleared(identity);
          clearedIdentityKeysRef.current.add(identity.identityKey);
          latestVerifiedIdentityByAddressRef.current.delete(identity.addressKey);
          deleteLatestVerifiedWaveReadIdentityByAddress(identity.addressKey);
          if (proxyRoleIdentityKey !== null) {
            latestVerifiedIdentityByProxyRoleRef.current.delete(proxyRoleIdentityKey);
          }
        }
        authByIdentityRef.current.clear();
        return;
      }
      if (verifiedIdentity) {
        authByIdentityRef.current.set(verifiedIdentity.identityKey, verifiedIdentity);
        latestVerifiedIdentityByAddressRef.current.set(verifiedIdentity.addressKey, verifiedIdentity);
        setLatestVerifiedWaveReadIdentityByAddress(verifiedIdentity);
        const proxyRoleIdentityKey_0 = getVerifiedProxyRoleIdentityKey(verifiedIdentity);
        if (proxyRoleIdentityKey_0 !== null) {
          latestVerifiedIdentityByProxyRoleRef.current.set(proxyRoleIdentityKey_0, verifiedIdentity);
        }
        flushPendingWaveReadRequests({
          verifiedIdentity,
          invalidateNotificationsRef
        });
        flushPendingClearedWaveReadRequests({
          verifiedIdentity,
          invalidateNotificationsRef
        });
      }
    };
    t2 = [authByIdentityRef, clearedIdentityKeysRef, invalidateNotificationsRef, latestVerifiedIdentityByAddressRef, latestVerifiedIdentityByProxyRoleRef, verifiedIdentity, walletAuth];
    $[0] = authByIdentityRef;
    $[1] = clearedIdentityKeysRef;
    $[2] = invalidateNotificationsRef;
    $[3] = latestVerifiedIdentityByAddressRef;
    $[4] = latestVerifiedIdentityByProxyRoleRef;
    $[5] = verifiedIdentity;
    $[6] = walletAuth;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useLayoutEffect(t1, t2);
};
const evictExpiredWaveReadIdentity = ({
  identity,
  cacheRefs
}: {
  readonly identity: WaveReadVerifiedIdentity;
  readonly cacheRefs: WaveReadCacheRefs;
}): void => {
  if (cacheRefs.authByIdentityRef.current.get(identity.identityKey) === identity) {
    cacheRefs.authByIdentityRef.current.delete(identity.identityKey);
  }
  if (cacheRefs.latestVerifiedIdentityByAddressRef.current.get(identity.addressKey) === identity) {
    cacheRefs.latestVerifiedIdentityByAddressRef.current.delete(identity.addressKey);
  }
  const proxyRoleIdentityKey = getVerifiedProxyRoleIdentityKey(identity);
  if (proxyRoleIdentityKey !== null && cacheRefs.latestVerifiedIdentityByProxyRoleRef.current.get(proxyRoleIdentityKey) === identity) {
    cacheRefs.latestVerifiedIdentityByProxyRoleRef.current.delete(proxyRoleIdentityKey);
  }
  deleteLatestVerifiedWaveReadIdentityIfCurrent(identity);
};
const getUsableCachedWaveReadIdentity = ({
  identity,
  cacheRefs
}: {
  readonly identity: WaveReadVerifiedIdentity | undefined;
  readonly cacheRefs: WaveReadCacheRefs;
}): WaveReadVerifiedIdentity | undefined => {
  if (!identity) {
    return undefined;
  }
  if (!isWaveReadJwtExpired(identity.jwtExpiresAt)) {
    return identity;
  }
  evictExpiredWaveReadIdentity({
    identity,
    cacheRefs
  });
  return undefined;
};
const getUsableLatestClearedWaveReadIdentity = ({
  addressKey,
  identityKey,
  cacheRefs
}: {
  readonly addressKey: string;
  readonly identityKey: string;
  readonly cacheRefs: WaveReadCacheRefs;
}): WaveReadVerifiedIdentity | undefined => {
  if (!cacheRefs.clearedIdentityKeysRef.current.has(identityKey)) {
    return undefined;
  }
  const localLatestIdentity = getUsableCachedWaveReadIdentity({
    identity: cacheRefs.latestVerifiedIdentityByAddressRef.current.get(addressKey),
    cacheRefs
  });
  if (localLatestIdentity) {
    return localLatestIdentity;
  }
  return getUsableCachedWaveReadIdentity({
    identity: getLatestVerifiedWaveReadIdentityByAddress(addressKey),
    cacheRefs
  });
};
export const useClearWaveReadStateOnAddressChange = addressKey => {
  const $ = _c(3);
  const previousAddressKeyRef = useRef(addressKey);
  let t0;
  let t1;
  if ($[0] !== addressKey) {
    t0 = () => {
      const previousAddressKey = previousAddressKeyRef.current;
      if (previousAddressKey !== null && previousAddressKey !== addressKey) {
        clearPendingWaveReadsForAddress(previousAddressKey);
      }
      previousAddressKeyRef.current = addressKey;
    };
    t1 = [addressKey];
    $[0] = addressKey;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
};
export const useClearWaveReadStateOnLastUnmount = addressKey => {
  const $ = _c(5);
  const latestAddressKeyRef = useRef(addressKey);
  let t0;
  let t1;
  if ($[0] !== addressKey) {
    t0 = () => {
      latestAddressKeyRef.current = addressKey;
    };
    t1 = [addressKey];
    $[0] = addressKey;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (clearWaveNotificationsReadStateTimeout !== null) {
        globalThis.clearTimeout(clearWaveNotificationsReadStateTimeout.timeout);
        clearWaveNotificationsReadStateTimeout = null;
        if (clearWaveNotificationsReadStateTimeout.addressKey !== null && clearWaveNotificationsReadStateTimeout.addressKey !== latestAddressKeyRef.current) {
          clearPendingWaveReadsForAddress(clearWaveNotificationsReadStateTimeout.addressKey);
        }
      }
      mountedWaveNotificationsReadMarkerHookCount = mountedWaveNotificationsReadMarkerHookCount + 1;
      return () => {
        mountedWaveNotificationsReadMarkerHookCount = mountedWaveNotificationsReadMarkerHookCount - 1;
        if (mountedWaveNotificationsReadMarkerHookCount > 0) {
          return;
        }
        mountedWaveNotificationsReadMarkerHookCount = 0;
        const cleanupAddressKey = latestAddressKeyRef.current;
        const timeout = globalThis.setTimeout(() => {
          if (clearWaveNotificationsReadStateTimeout?.timeout === timeout) {
            clearWaveNotificationsReadStateTimeout = null;
          }
          if (mountedWaveNotificationsReadMarkerHookCount === 0) {
            clearAllWaveReadState();
          }
        }, 0);
        clearWaveNotificationsReadStateTimeout = {
          timeout,
          addressKey: cleanupAddressKey
        };
      };
    };
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useLayoutEffect(t2, t3);
};
const createWaveReadSendIntent = ({
  shouldSend,
  retryContext
}: {
  readonly shouldSend: MarkWaveNotificationsReadOptions["shouldSend"];
  readonly retryContext: WaveReadSendRetryContext | undefined;
}): WaveReadSendIntent => ({
  shouldSend,
  retryContext
});
const getWaveReadSendRetryContext = ({
  addressKey,
  activeProfileProxyId,
  proxyCreatorId,
  identityKey,
  requestKey,
  waveId,
  addressEpoch,
  cacheRefs,
  queueIfBlocked
}: {
  readonly addressKey: string;
  readonly activeProfileProxyId: string | null;
  readonly proxyCreatorId: string | null;
  readonly identityKey: string;
  readonly requestKey: string;
  readonly waveId: string;
  readonly addressEpoch: WaveReadAddressEpoch;
  readonly cacheRefs: WaveReadCacheRefs;
  readonly queueIfBlocked: boolean;
}): WaveReadSendRetryContext | undefined => {
  if (!queueIfBlocked) {
    return undefined;
  }
  return {
    addressKey,
    activeProfileProxyId,
    proxyCreatorId,
    identityKey,
    requestKey,
    waveId,
    addressEpoch,
    latestAddressEpochRef: cacheRefs.latestAddressEpochRef
  };
};
const markTemporaryProxyRoleWaveRead = ({
  waveId,
  addressKey,
  addressEpoch,
  temporaryProxyRoleIdentity,
  cacheRefs,
  options
}: {
  readonly waveId: string;
  readonly addressKey: string;
  readonly addressEpoch: WaveReadAddressEpoch;
  readonly temporaryProxyRoleIdentity: WaveReadTemporaryProxyRoleIdentity;
  readonly cacheRefs: WaveReadCacheRefs;
  readonly options: MarkWaveNotificationsReadOptions | undefined;
}): Promise<MarkWaveNotificationsReadResult> => {
  const latestVerifiedProxyRoleIdentity = getUsableCachedWaveReadIdentity({
    identity: cacheRefs.latestVerifiedIdentityByProxyRoleRef.current.get(temporaryProxyRoleIdentity.identityKey),
    cacheRefs
  });
  if (latestVerifiedProxyRoleIdentity) {
    const loadedProxyRequestKey = getWaveReadRequestKey({
      addressKey: latestVerifiedProxyRoleIdentity.addressKey,
      activeProfileProxyId: latestVerifiedProxyRoleIdentity.activeProfileProxyId,
      waveId
    });
    return markWaveReadWithAuthHeaders({
      waveId,
      addressKey: latestVerifiedProxyRoleIdentity.addressKey,
      requestKey: loadedProxyRequestKey,
      authHeaders: latestVerifiedProxyRoleIdentity.authHeaders,
      jwtExpiresAt: latestVerifiedProxyRoleIdentity.jwtExpiresAt,
      invalidateNotificationsRef: cacheRefs.invalidateNotificationsRef,
      sendIntents: [createWaveReadSendIntent({
        shouldSend: options?.shouldSend,
        retryContext: getWaveReadSendRetryContext({
          addressKey: latestVerifiedProxyRoleIdentity.addressKey,
          activeProfileProxyId: latestVerifiedProxyRoleIdentity.activeProfileProxyId,
          proxyCreatorId: null,
          identityKey: latestVerifiedProxyRoleIdentity.identityKey,
          requestKey: loadedProxyRequestKey,
          waveId,
          addressEpoch,
          cacheRefs,
          queueIfBlocked: options?.queueIfBlocked ?? true
        })
      })]
    });
  }
  const proxyRoleRequestKey = getWaveReadProxyRoleRequestKey({
    addressKey,
    proxyCreatorId: temporaryProxyRoleIdentity.proxyCreatorId,
    waveId
  });
  return enqueuePendingWaveReadRequest({
    addressKey,
    activeProfileProxyId: null,
    proxyCreatorId: temporaryProxyRoleIdentity.proxyCreatorId,
    identityKey: temporaryProxyRoleIdentity.identityKey,
    requestKey: proxyRoleRequestKey,
    waveId,
    addressEpoch,
    latestAddressEpochRef: cacheRefs.latestAddressEpochRef,
    shouldSend: options?.shouldSend,
    queueIfBlocked: options?.queueIfBlocked ?? true
  });
};
export const markWaveReadFromCache = ({
  waveId,
  addressKey,
  activeProfileProxyId,
  identityKey,
  addressEpoch,
  cacheRefs,
  options
}: {
  readonly waveId: string;
  readonly addressKey: string | null;
  readonly activeProfileProxyId: string | null;
  readonly identityKey: string;
  readonly addressEpoch: WaveReadAddressEpoch;
  readonly cacheRefs: WaveReadCacheRefs;
  readonly options: MarkWaveNotificationsReadOptions | undefined;
}): Promise<MarkWaveNotificationsReadResult> => {
  if (addressKey === null) {
    return Promise.resolve("skipped");
  }
  const queueIfBlocked = options?.queueIfBlocked ?? true;
  const staleAddressEpochResult = getStaleAddressEpochWaveReadResult({
    addressEpoch,
    latestAddressEpochRef: cacheRefs.latestAddressEpochRef,
    queueIfBlocked
  });
  if (staleAddressEpochResult) {
    return staleAddressEpochResult;
  }
  const requestKey = getWaveReadRequestKey({
    addressKey,
    activeProfileProxyId,
    waveId
  });
  const verifiedCachedIdentity = getUsableCachedWaveReadIdentity({
    identity: cacheRefs.authByIdentityRef.current.get(identityKey),
    cacheRefs
  });
  if (verifiedCachedIdentity) {
    return markWaveReadWithAuthHeaders({
      waveId,
      addressKey: verifiedCachedIdentity.addressKey,
      requestKey,
      authHeaders: verifiedCachedIdentity.authHeaders,
      jwtExpiresAt: verifiedCachedIdentity.jwtExpiresAt,
      invalidateNotificationsRef: cacheRefs.invalidateNotificationsRef,
      sendIntents: [createWaveReadSendIntent({
        shouldSend: options?.shouldSend,
        retryContext: getWaveReadSendRetryContext({
          addressKey: verifiedCachedIdentity.addressKey,
          activeProfileProxyId,
          proxyCreatorId: null,
          identityKey: verifiedCachedIdentity.identityKey,
          requestKey,
          waveId,
          addressEpoch,
          cacheRefs,
          queueIfBlocked
        })
      })]
    });
  }
  const latestVerifiedIdentity = getUsableLatestClearedWaveReadIdentity({
    addressKey,
    identityKey,
    cacheRefs
  });
  if (latestVerifiedIdentity?.identityKey === identityKey) {
    return markWaveReadWithAuthHeaders({
      waveId,
      addressKey: latestVerifiedIdentity.addressKey,
      requestKey,
      authHeaders: latestVerifiedIdentity.authHeaders,
      jwtExpiresAt: latestVerifiedIdentity.jwtExpiresAt,
      invalidateNotificationsRef: cacheRefs.invalidateNotificationsRef,
      sendIntents: [createWaveReadSendIntent({
        shouldSend: options?.shouldSend,
        retryContext: getWaveReadSendRetryContext({
          addressKey: latestVerifiedIdentity.addressKey,
          activeProfileProxyId,
          proxyCreatorId: null,
          identityKey: latestVerifiedIdentity.identityKey,
          requestKey,
          waveId,
          addressEpoch,
          cacheRefs,
          queueIfBlocked
        })
      })]
    });
  }
  const temporaryProxyRoleIdentity = cacheRefs.temporaryProxyRoleIdentityByIdentityRef.current.get(identityKey);
  if (temporaryProxyRoleIdentity) {
    return markTemporaryProxyRoleWaveRead({
      waveId,
      addressKey,
      addressEpoch,
      temporaryProxyRoleIdentity,
      cacheRefs,
      options
    });
  }
  return enqueuePendingWaveReadRequest({
    addressKey,
    activeProfileProxyId,
    proxyCreatorId: null,
    identityKey,
    requestKey,
    waveId,
    addressEpoch,
    latestAddressEpochRef: cacheRefs.latestAddressEpochRef,
    shouldSend: options?.shouldSend,
    queueIfBlocked
  });
};